import { useState } from "react";
import { ShieldAlert, Mail, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const initial = { name: "", email: "", company: "", role: "", message: "", company_website: "" };

const CONTACT_ROWS = [
  { k: "Businesses", v: "Talk to us about payments, ordering, tipping, reservations, and analytics." },
  { k: "Support", v: "Questions about MONOE Pay while we prepare for launch." },
  { k: "Legal & privacy", v: "See the Legal Center for terms, privacy, and integration policies." },
];

function useContactForm() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const isValid = () =>
    form.name.trim().length >= 2 && form.email.includes("@") && form.message.trim().length >= 10;

  const submit = async (e) => {
    e.preventDefault();
    if (!isValid()) {
      toast.error("Please complete the form.", { description: "Name, a valid email, and a message (10+ characters) are required." });
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await apiClient.post("/contact", form);
      setReference(data.reference);
      setForm(initial);
      toast.success("Message sent.", { description: `Reference ${data.reference}. We'll be in touch.` });
    } catch (err) {
      const msg = err?.response?.status === 429
        ? "Too many messages. Please try again in a little while."
        : "Something went wrong. Please try again.";
      toast.error("Not sent", { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return { form, submitting, reference, update, submit, reset: () => setReference(null) };
}

const ContactSidebar = () => (
  <div className="lg:col-span-2">
    <Reveal>
      <div className="flex items-start gap-3 rounded-lg border border-border bg-card p-5">
        <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-foreground" />
        <p className="text-sm leading-relaxed text-muted-foreground" data-testid="contact-security-notice">
          <span className="font-semibold text-foreground">Security notice.</span> Never submit
          passwords, PINs, complete card numbers, CVVs, bank login credentials, or other
          sensitive authentication information through this form. MONOE will never ask for
          them.
        </p>
      </div>
    </Reveal>
    <Reveal delay={0.05}>
      <div className="mt-6 space-y-5 rounded-lg border border-border bg-card p-6">
        {CONTACT_ROWS.map((row) => (
          <div key={row.k}>
            <p className="eyebrow text-muted-foreground">{row.k}</p>
            <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{row.v}</p>
          </div>
        ))}
      </div>
    </Reveal>
  </div>
);

const ContactSuccess = ({ reference, onReset }) => (
  <Reveal>
    <div data-testid="contact-success" className="rounded-lg border border-border bg-card p-8 text-center">
      <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
      <h3 className="mt-4 font-display text-xl font-semibold">Message received</h3>
      <p className="mt-2 text-sm text-muted-foreground">
        Your reference is <span className="font-mono-ui font-medium text-foreground">{reference}</span>. We'll get back to you at the email you provided.
      </p>
      <button type="button" onClick={onReset} className="mt-6 rounded-md border border-border px-5 py-2.5 text-sm font-medium transition-colors hover:bg-accent">
        Send another message
      </button>
    </div>
  </Reveal>
);

const ContactForm = ({ form, submitting, update, onSubmit }) => (
  <Reveal>
    <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-5 rounded-lg border border-border bg-card p-6 sm:p-8">
      {/* Honeypot: hidden from users, named to bait bots */}
      <input
        type="text"
        name="company_website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={form.company_website}
        onChange={update("company_website")}
        style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
      />
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="name">Full name</Label>
          <Input id="name" data-testid="contact-form-input-name" value={form.name} onChange={update("name")} placeholder="Jordan Rivera" required />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Business email</Label>
          <Input id="email" type="email" data-testid="contact-form-input-email" value={form.email} onChange={update("email")} placeholder="you@business.com" required />
        </div>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="company">Company / business</Label>
          <Input id="company" data-testid="contact-form-input-company" value={form.company} onChange={update("company")} placeholder="Optional" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="role">Role / merchant type</Label>
          <Input id="role" data-testid="contact-form-input-role" value={form.role} onChange={update("role")} placeholder="Optional" />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="message">Message</Label>
        <Textarea id="message" data-testid="contact-form-input-message" value={form.message} onChange={update("message")} placeholder="How can we help?" rows={5} required />
      </div>
      <button
        type="submit"
        disabled={submitting}
        data-testid="contact-form-submit-button"
        className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-foreground px-6 py-3 text-sm font-semibold text-background transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60 sm:w-auto"
      >
        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
        {submitting ? "Sending..." : "Send message"}
      </button>
    </form>
  </Reveal>
);

const Contact = () => {
  useSeo({
    title: "Contact",
    description: "Get in touch with MONOE Pay LLC. Support: support@monoepay.com.",
    path: "/contact",
  });

  const { form, submitting, reference, update, submit, reset } = useContactForm();

  return (
    <div>
      <section className="relative overflow-hidden border-b border-border">
        <div className="pointer-events-none absolute inset-0 grid-bg opacity-50 [mask-image:radial-gradient(ellipse_at_top,black,transparent_75%)]" />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
          <Reveal><Eyebrow>Contact</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-4 max-w-3xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl">
              Get in touch with MONOE Pay LLC
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <a href="mailto:support@monoepay.com" data-testid="contact-support-email" className="mt-6 inline-flex items-center gap-2 font-mono-ui text-sm text-foreground underline underline-offset-4">
              <Mail className="h-4 w-4" /> support@monoepay.com
            </a>
          </Reveal>
        </div>
      </section>

      <section>
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-5 lg:gap-16 lg:px-8 lg:py-24">
          <ContactSidebar />
          <div className="lg:col-span-3">
            {reference ? (
              <ContactSuccess reference={reference} onReset={reset} />
            ) : (
              <ContactForm form={form} submitting={submitting} update={update} onSubmit={submit} />
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
